/*
 * Created Date: Sunday April 3rd 2022
 * Author: Oskar Karnblad
 * License: Proprietary to Scaffcalc AB
 *
 * Copyright (c) 2022 Scaffcalc AB
 */
import { ButtonGroup, Menu, MenuItem, Stack, SvgIcon } from "@mui/material";
import { ArrowDropDown, ArrowDropUp, InfoOutlined } from "@mui/icons-material";
import { Button, ToolTip } from "@scaffcalc/packages-ui";
import { useEffect, useRef, useState } from "react";

export interface SplitButtonOption {
  value: string;
  text: string;
  startIcon?: any;
  endIcon?: any;
  labelColor?: string;
  selected?: boolean;
  infoContent?: any;
  infoButton?: boolean;
  disabled?: boolean;
}

export interface CustomSplitButtonProps {
  color?: "green" | "blue" | "grey" | "red";
  onClickButton?: any;
  startIcon?: any;
  style?: any;
  defaultText: string | undefined;
  defaultStartIcon: any;
  id?: string;
  type?: "round" | "square";
  selected?: boolean;
  options: SplitButtonOption[];
  setActiveValue?: string | null;
}

const CustomSplitButton = ({
  color,
  onClickButton,
  startIcon,
  defaultText,
  defaultStartIcon,
  id,
  type,
  selected,
  options,
  style,
  setActiveValue,
  ...other
}: CustomSplitButtonProps) => {
  const anchorRef = useRef<HTMLDivElement>(null);

  const [activeOption, setActiveOption] =
    useState<SplitButtonOption | null>(null);

  const [expanded, setExpanded] = useState<boolean>(false);

  const [dropDownIcon, setDropDownIcon] = useState<JSX.Element>(
    <ArrowDropDown />
  );

  const handleOnClickOpen = () => {
    setExpanded(true);
    setDropDownIcon(<ArrowDropUp />);
  };

  const handleOnClose = () => {
    setExpanded(false);
    setDropDownIcon(<ArrowDropDown />);
  };

  const handleOnClickOption = (option: SplitButtonOption) => {
    if (option.disabled) return;
    setActiveOption(option);
    onClickButton(option.value);
    handleOnClose();
  };

  useEffect(() => {
    if (setActiveValue) {
      const option = options.find((option) => option.value === setActiveValue);
      if (!option) return;
      setActiveOption(option);
      onClickButton(option.value);
    }
  }, [onClickButton, options, setActiveValue]);

  return (
    <>
      <ButtonGroup
        ref={anchorRef}
        id={id}
        sx={{
          gap: 0.25,
          width: "100%",
          ...style,
        }}
      >
        <Button
          color={color}
          onClick={() =>
            activeOption ? onClickButton(activeOption.value) : setExpanded(true)
          }
          align="left"
          fullWidth
          text={activeOption ? activeOption.text : defaultText}
          labelColor={activeOption?.labelColor}
          startIcon={
            activeOption?.startIcon ? activeOption.startIcon : defaultStartIcon
          }
          endIcon={activeOption?.endIcon ? activeOption.endIcon : undefined}
          selected={
            options.find((option) => option.value === activeOption?.value)
              ?.selected
          }
          disabled={activeOption?.disabled}
          infoContent={activeOption?.infoButton && activeOption?.infoContent}
        />
        <Button
          color={color}
          onClick={handleOnClickOpen}
          sx={{
            minWidth: "auto",
            paddingInline: "10px",
            "& .MuiButton-endIcon": {
              margin: 0,
            },
            height: "100%",
          }}
          selected={
            options.find((option) => option.value === activeOption?.value)
              ?.selected
          }
          endIcon={dropDownIcon}
        />
      </ButtonGroup>
      <Menu
        elevation={0}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        MenuListProps={{ sx: { p: 0 } }}
        anchorEl={anchorRef.current}
        open={expanded}
        onClose={handleOnClose}
        sx={{
          position: "absolute",
          ".MuiMenu-paper": {
            backgroundColor: "secondary.main",
          },
        }}
      >
        {options.map((option, idx) => (
          <MenuItem
            key={idx}
            onClick={() => {
              handleOnClickOption(option);
            }}
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1,
              paddingRight: option.disabled ? 1 : option.labelColor ? 2 : 1,
              paddingLeft: option.labelColor ? 2 : 1,
              marginTop: idx !== 0 ? 0.25 : undefined,
            }}
          >
            {option.labelColor && (
              <Stack
                style={{
                  width: 8,
                  height: "100%",
                  left: 0,
                  position: "absolute",
                  backgroundColor: option.labelColor,
                  zIndex: 1,
                  filter: option.disabled ? "saturate(0.6) " : "none",
                }}
              />
            )}
            {option.startIcon && (
              <SvgIcon
                sx={{
                  color: option.disabled ? "text.disabled" : "text.primary",
                }}
              >
                {option.startIcon}
              </SvgIcon>
            )}
            <Stack
              sx={{
                flexGrow: 1,
                color: option.disabled ? "text.disabled" : "text.primary",
              }}
            >
              {option.text}
            </Stack>
            {option.endIcon && (
              <SvgIcon
                sx={{
                  color: option.disabled ? "text.disabled" : "text.primary",
                  width: "20px",
                  height: "20px",
                }}
              >
                {option.endIcon}
              </SvgIcon>
            )}
            {option.disabled && (
              <ToolTip content={option.infoContent}>
                <InfoOutlined
                  sx={{
                    color: "text.disabled",
                    cursor: "pointer",
                    marginLeft: "12px",
                    width: "20px",
                    height: "20px",
                  }}
                />
              </ToolTip>
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default CustomSplitButton;
